import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { SignOut, ArrowLeft, Envelope, Phone, Buildings, MagnifyingGlass, ArrowClockwise } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function formatDate(d) {
  try {
    const dt = typeof d === 'string' ? new Date(d) : d;
    return dt.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return String(d);
  }
}

export default function AdminInquiries() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/admin/login', { replace: true });
    }
  }, [user, loading, navigate]);

  const fetchData = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await axios.get(`${API}/contact?limit=500`, { withCredentials: true });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to load inquiries';
      setError(typeof detail === 'string' ? detail : 'Failed to load');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (user) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0A1128] flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-white/20 border-t-[#4F6355] rounded-full animate-spin" />
      </div>
    );
  }

  const filtered = items.filter((i) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (i.name || '').toLowerCase().includes(q) ||
      (i.email || '').toLowerCase().includes(q) ||
      (i.company || '').toLowerCase().includes(q) ||
      (i.message || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-[#0A1128] text-white">
      <header className="border-b border-white/10 bg-[#0A1128]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors">
              <ArrowLeft size={16} weight="bold" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold">Site</span>
            </Link>
            <span className="text-white/15">/</span>
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-white">
              CDPA · Admin · Inquiries
            </div>
          </div>
          <div className="flex items-center gap-4">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
            ) : null}
            <div className="hidden sm:flex flex-col leading-none">
              <span className="text-sm font-semibold text-white">{user.name}</span>
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#94A3B8] mt-0.5">{user.email}</span>
            </div>
            <button
              data-testid="admin-logout"
              onClick={async () => { await logout(); navigate('/admin/login', { replace: true }); }}
              className="btn-outline-dark inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-bold"
            >
              <SignOut size={14} weight="bold" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-[#4F6355]">Inquiries</div>
            <h1 className="font-display font-bold tracking-tighter mt-3 text-3xl md:text-4xl text-white">
              {items.length} total contact submission{items.length === 1 ? '' : 's'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input
                data-testid="admin-search-input"
                type="text"
                placeholder="Search name, email, company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2.5 w-72 bg-[#151F32] border border-white/10 text-sm text-white placeholder:text-[#475569] focus:outline-none focus:border-[#4F6355] transition-colors"
              />
            </div>
            <button
              data-testid="admin-refresh"
              onClick={fetchData}
              className="btn-outline-dark inline-flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.22em] uppercase font-bold"
            >
              <ArrowClockwise size={14} weight="bold" className={busy ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 px-4 py-3 border border-red-400/30 bg-red-900/20 text-red-200 text-sm">
            {error}
          </div>
        )}

        <div className="border border-white/10 bg-[#151F32]/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table data-testid="admin-inquiries-table" className="w-full text-sm">
              <thead className="bg-[#151F32] text-[10px] tracking-[0.22em] uppercase text-[#94A3B8]">
                <tr>
                  <th className="text-left px-5 py-4 font-semibold">Submitted</th>
                  <th className="text-left px-5 py-4 font-semibold">Name</th>
                  <th className="text-left px-5 py-4 font-semibold">Company</th>
                  <th className="text-left px-5 py-4 font-semibold">Email</th>
                  <th className="text-left px-5 py-4 font-semibold">Phone</th>
                  <th className="text-left px-5 py-4 font-semibold">Email sent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {!busy && filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-16 text-center text-[#94A3B8]">
                      {items.length === 0 ? 'No inquiries yet.' : 'No matches for current search.'}
                    </td>
                  </tr>
                )}
                {filtered.map((it) => (
                  <tr
                    key={it.id}
                    onClick={() => setSelected(it)}
                    className="hover:bg-white/5 transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-4 font-mono text-[11px] text-[#94A3B8] whitespace-nowrap">
                      {formatDate(it.created_at)}
                    </td>
                    <td className="px-5 py-4 font-semibold text-white">{it.name}</td>
                    <td className="px-5 py-4 text-[#94A3B8]">{it.company || '—'}</td>
                    <td className="px-5 py-4 text-[#94A3B8]">{it.email}</td>
                    <td className="px-5 py-4 text-[#94A3B8]">{it.phone || '—'}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-bold ${
                          it.email_sent
                            ? 'bg-[#4F6355]/20 text-[#A4B8AC] border border-[#4F6355]/40'
                            : 'bg-white/5 text-[#94A3B8] border border-white/10'
                        }`}
                      >
                        {it.email_sent ? 'Delivered' : 'Stored'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* Detail drawer */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex justify-end"
          onClick={() => setSelected(null)}
        >
          <div
            className="w-full max-w-xl bg-[#0A1128] border-l border-white/10 h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-8 py-6 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#4F6355]">
                  Inquiry · {selected.id.slice(0, 8)}
                </div>
                <h3 className="mt-2 font-display font-bold text-2xl tracking-tight text-white">
                  {selected.name}
                </h3>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-[#94A3B8] hover:text-white text-xl"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="px-8 py-6 space-y-5">
              <Meta Icon={Buildings} label="Company" value={selected.company || '—'} />
              <Meta Icon={Envelope} label="Email" value={selected.email} link={`mailto:${selected.email}`} />
              <Meta Icon={Phone} label="Phone" value={selected.phone || '—'} link={selected.phone ? `tel:${selected.phone}` : undefined} />
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#94A3B8] font-bold">Submitted</div>
                <div className="text-sm text-white mt-2 font-mono">{formatDate(selected.created_at)}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#94A3B8] font-bold">Message</div>
                <div className="mt-3 p-4 bg-[#151F32] border-l-2 border-[#4F6355] text-sm text-white leading-relaxed whitespace-pre-wrap">
                  {selected.message}
                </div>
              </div>
              {selected.email_error && (
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#94A3B8] font-bold">Email delivery</div>
                  <div className="mt-2 text-xs text-red-300 font-mono">{selected.email_error}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Meta({ Icon, label, value, link }) {
  const Wrap = link ? 'a' : 'div';
  return (
    <Wrap href={link} className={`flex items-start gap-4 ${link ? 'hover:text-[#4F6355] transition-colors' : ''}`}>
      <div className="w-10 h-10 flex items-center justify-center border border-white/10 bg-white/5 text-[#4F6355] shrink-0">
        <Icon size={16} weight="duotone" />
      </div>
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#94A3B8] font-bold">{label}</div>
        <div className="text-sm text-white mt-1">{value}</div>
      </div>
    </Wrap>
  );
}
